import { notification } from 'antd'
import store from 'store'
import i18n from 'i18next'
import { store as reduxStore } from 'index'
import { authClient } from 'myNet'
import { processError } from 'utils'
import { errorBase } from 'utils/ui'
import history from 'history'
import { setTempEnvParam } from 'env'
import { captureForcedLogout } from 'services/sentryLog'

const updateInfo = {
  isUpdating: false,
  currentQuery: null,
  waitingReq: [],
}

let isIpBlockedHandling = false

// 870(IP 차단)·879(해외 차단) 공통 처리 — 로그인 이후 전역(customFetch)과
// 로그인 체인(pUtils.login) 양쪽에서 사용한다.
// skipLogout: 로그인 도중에는 LOGOUT saga가 SAML 워크스페이스에서 IdP로 하드 리다이렉트해
// /ip-blocked 이동을 덮어쓰므로 스킵하고, 토큰 정리는 호출측(pUtils)이 로컬로 수행 (QA round1).
export const handleAccessBlocked = (code, { skipLogout = false, requestUrl } = {}) => {
  try {
    const bc = new BroadcastChannel('ip-blocked')
    bc.postMessage({ type: 'ip-blocked' })
    bc.close()
  } catch (e) {
    console.log('BroadcastChannel not supported', e)
  }

  setTempEnvParam('ipBlocked', {
    blocked: true,
    timestamp: Date.now(),
    reason: code === 879 ? 'overseas' : 'ip',
  })
  history.push('/ip-blocked')

  if (!skipLogout && store.get('app.user.token')) {
    captureForcedLogout('jwt.custom-fetch.ip-blocked', {
      requestUrl,
      responseCode: code,
    })
    reduxStore.dispatch({
      type: 'user/LOGOUT',
    })
  }
}

// 강제 로그아웃 공통 처리 — 인증 무효화(821 SSO 방식 변경) 등 HTTP 200 + code 로 오는
// 서버 세션 종료 신호를 안내 팝업(notification) + 로그아웃으로 처리. i18n 키(workspace.error.{code}).
// (822 로그인 차단은 restClient가 licenseTerminated → layouts 중앙 모달로 처리하므로 여기 미사용.)
const handleForcedLogout = (code, captureLabel, uri) => {
  notification.info({
    message: i18n.t(`workspace.error.${code}.title`),
    description: i18n.t(`workspace.error.${code}.description`),
  })
  captureForcedLogout(captureLabel, {
    requestUrl: uri,
    responseCode: code,
  })
  reduxStore.dispatch({ type: 'user/LOGOUT' })
}

// 의존성 zero 모듈로 이동 (B′ dbe_scope_jwt_fallback.md §3.2) — 기존 소비처 호환 re-export
export { decodeJwtPayload } from './decode'

// 익스텐션이 race 에서 진 웹에게 새 refresh token 을 써넣어 줄 때까지 기다리는
// window. OffscreenManager.startWriteSync 의 iframe 생성 + onload + 1s setTimeout
// + postMessage + content script write 가 누적되어 평균 2~3초 걸리는데, 3000ms
// timeout 으로는 빡빡해 race 회복률이 낮았음. Sentry forced-logout 이벤트
// (rescueAttempted=true, rescueDurationMs=3000) 가 모두 timeout 까지 가서 logout
// 된 패턴을 보고 5000ms 로 상향. 진짜 만료 케이스의 logout 만 5초 지연되고
// race 케이스는 회복 가능성 ↑.
export const RESCUE_POLL_TIMEOUT_MS = 5000

export function pollForRefreshTokenChange(
  originalRefreshToken,
  timeoutMs = RESCUE_POLL_TIMEOUT_MS,
) {
  return new Promise((resolve) => {
    const startedAt = Date.now()
    const intervalId = setInterval(() => {
      const current = store.get('app.user.refreshToken')
      if (current && current !== originalRefreshToken) {
        clearInterval(intervalId)
        resolve(current)
        return
      }
      if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(intervalId)
        resolve(null)
      }
    }, 150)
  })
}

export function updateToken() {
  const refreshToken = store.get('app.user.refreshToken')
  let ret = null

  if (refreshToken != null) {
    if (updateInfo.isUpdating === false) {
      store.remove('app.user.token')
      updateInfo.isUpdating = true

      const finalizeSuccess = (data) => {
        store.set('app.user.token', data.access_token)
        store.set('app.user.refreshExpiresIn', data.expires_in)
        store.set('app.user.refreshToken', data.refresh_token)
        updateInfo.waitingReq.forEach((promise) => {
          promise.resolve(data.access_token)
        })
        updateInfo.waitingReq = []
        updateInfo.isUpdating = false
        updateInfo.currentQuery = null
        return data.access_token
      }

      const finalizeFailure = () => {
        updateInfo.waitingReq.forEach((promise) => {
          promise.reject()
        })
        updateInfo.waitingReq = []
        updateInfo.isUpdating = false
        updateInfo.currentQuery = null
        captureForcedLogout('jwt.update-token.finalize-failure', {
          rescueAttempted: true,
          rescueDurationMs: RESCUE_POLL_TIMEOUT_MS,
          hasRefreshToken: !!store.get('app.user.refreshToken'),
        })
        reduxStore.dispatch({ type: 'user/LOGOUT' })
      }

      updateInfo.currentQuery = authClient
        .post('/auth/refresh', {
          refresh_token: refreshToken,
        })
        .then((res) => {
          if (res.data.data.access_token != null) {
            console.log(' updateToken - success : ', updateInfo.waitingReq.length)
            return finalizeSuccess(res.data.data)
          }
          return null
        })
        .catch(async (err) => {
          console.log(' updateToken - fail : ', updateInfo.waitingReq.length, err)

          // Rescue: 익스텐션이 같은 refresh token 으로 먼저 refresh 해 우리
          // 토큰이 revoked 된 경우, 곧 (또는 이미) WRITE_TOKENS 로 새 토큰이
          // localStorage 에 도착. 즉시 logout 하지 말고 짧게 기다려보고
          // 발견되면 그 토큰으로 1회 재시도.
          const rescued = await pollForRefreshTokenChange(refreshToken)
          if (rescued) {
            console.log(
              ' updateToken - rescue: detected new refreshToken from extension, retrying once',
            )
            try {
              const retryRes = await authClient.post('/auth/refresh', {
                refresh_token: rescued,
              })
              if (retryRes?.data?.data?.access_token != null) {
                console.log(' updateToken - rescue success')
                return finalizeSuccess(retryRes.data.data)
              }
            } catch (retryErr) {
              console.log(' updateToken - rescue retry also failed', retryErr)
            }
          }

          if (err.cause != null) {
            const { statusCode, result } = err.cause
            const { errors } = result
            console.log(' ==== updateToken - errors ', statusCode, errors)
          }

          finalizeFailure()
          return null
        })

      ret = updateInfo.currentQuery
    } else {
      const retPromise = new Promise((resolve, reject) => {
        updateInfo.waitingReq.push({
          resolve,
          reject,
        })
      })
      ret = retPromise
    }
  }
  return ret
}

function callUpdateToken(uri, options) {
  let retPromise = null
  let ret = false

  const updateHandle = updateToken()
  if (updateHandle != null) {
    retPromise = new Promise((resolve, reject) => {
      updateHandle
        .then((token) => {
          console.log(' new token : ', token)
          if (token != null) {
            options.headers.authorization = `JWT ${token}`
            console.log(' ==== call After token Updated - ', uri, options)
            fetch(uri, options).then((res2) => {
              resolve(res2)
            })
          }
        })
        .catch(() => {
          reject()
        })
    })
    ret = true
  } else {
    ret = false
  }

  return {
    retPromise,
    ret,
  }
}

export function customFetchWithToken(uri, options) {
  let isTokenEmpty = null
  const token = store.get('app.user.token')

  if (token == null || token.trim().length === 0) {
    isTokenEmpty = true
  }

  return customFetch(uri, options, isTokenEmpty)
}

export function customFetch(uri, options, isTokenEmpty) {
  console.log(' ==== customFetch ', uri, options, isTokenEmpty, this)

  if (isTokenEmpty === true) {
    console.log(' ==== customFetch - token is Empty~! ')
    const callRet = callUpdateToken(uri, options)
    return callRet.retPromise
  }

  const initialRequest = fetch(uri, options)

  let responseData = null
  return initialRequest
    .then((response) => {
      responseData = response
      if (response.status !== 204 && response.status !== 304) {
        // content가 없거나, not modified면 에러발생.
        const ret = response.clone().json()
        console.log(' ==== fetch result ', response, ret)
        return ret
      }
      return null
    })
    .then((jsonBody) => {
      if (responseData.status === 429) {
        errorBase({
          title: i18n.t('workspace.error.429.title'),
          content: i18n.t('workspace.error.429.description'),
        })
        return responseData
      }

      if (jsonBody && [870, 879].includes(jsonBody.code)) {
        // IP(870)/해외(879) 차단 — auth 레이아웃이 아닌 경우에만 처리
        // (로그인 체인의 차단은 pUtils.login이 handleAccessBlocked로 자체 처리)
        if (
          !isIpBlockedHandling &&
          window.location.pathname !== '/ip-blocked' &&
          !window.location.pathname.startsWith('/auth')
        ) {
          isIpBlockedHandling = true
          handleAccessBlocked(jsonBody.code, { requestUrl: uri })
        }

        return responseData
      }

      // 인증 무효화(821 SSO 방식 변경) — HTTP 200 + code 는 기존 에러 진입 조건을
      // 통과하지 못하므로 독립 처리. 안내 문구(notification) 후 강제 로그아웃.
      // (822 로그인 차단은 890/891과 동일하게 restClient가 licenseTerminated로 처리 →
      //  layouts 중앙 모달. jwt에서 처리하면 이중 반응이 되어 여기서는 통과시킨다.)
      if (jsonBody && jsonBody.code === 821) {
        handleForcedLogout(821, 'jwt.custom-fetch.auth-method-changed', uri)
        return responseData
      }

      if (
        (responseData.status >= 400 && jsonBody && jsonBody.code !== 200) ||
        (responseData.status === 200 && jsonBody && jsonBody.code === 401)
      ) {
        const error = jsonBody
        let ret = {}

        processError(error, (code, message) => {
          if (
            code === 401 ||
            message.indexOf('Signature has expired') > -1 ||
            message.indexOf('jwt expired') > -1
          ) {
            console.log(' !!! Signature has expired ')
            console.log('error code ', code, message)

            ret = callUpdateToken(uri, options)
            if (ret.retPromise == null) {
              notification.warning({
                message: code,
                description: message,
              })
            }
          } else if (code === 500 || message === 'Not authenticated' || code === 509) {
            // 익스텐션과의 refresh race 가능성: 즉시 LOGOUT 대신 401 분기와
            // 같은 callUpdateToken 위임 — 내부 updateToken catch 분기의
            // pollForRefreshTokenChange 가 새 refresh token 도착을 기다린다.
            // race 였으면 1회 재시도로 회복, 정말 만료면 3s 후 동일하게 logout.
            ret = callUpdateToken(uri, options)
            if (ret.retPromise == null) {
              captureForcedLogout('jwt.custom-fetch.no-refresh-token', {
                requestUrl: uri,
                responseCode: code,
                responseMessage: message,
                hasRefreshToken: false,
              })
              reduxStore.dispatch({
                type: 'user/LOGOUT',
              })
              throw new Error(code === 509 ? i18n.t('workspace.error.509') : message)
            }
          }
          return ret.ret
        })

        if (ret.retPromise != null) {
          return ret.retPromise
        }
      }

      return responseData
    })
}

const jwt = {
  customFetch,
}

export default jwt
